import { Link } from 'react-router-dom';
import FavoriteButton from '../button/FavoriteButton';
import Alloggio from '../../assets/icone/card/iconaAlloggioOut.svg';
import Single from '../../assets/icone/card/iconaSingleFill.svg';
import ShareButton from '../button/ShareButton';
import { useTranslation } from 'react-i18next';
import React, { useState, useRef, useEffect } from 'react';
import CountdownPreview from '../utils/CountDownPreview';
import Bambini from '../../assets/icone/card/iconaFamigliaFill.svg';
import { getHotelImageUrl } from '../../api';

const RoomCard = ({ ticket }) => {
    const { t } = useTranslation();
    const [transformStyle, setTransformStyle] = useState('');
    const cardRef = useRef(null);
    const requestRef = useRef(null);


    const convertCurrencySymbol = (currency) => {
        switch (currency.toUpperCase()) {
            case 'EUR':
                return '€';
            case 'USD':
                return '$';
            case 'GBP':
                return '£';
            default:
                return currency;
        }
    };
    const getHighestOffer = (offers) => {
        if (!offers || offers.length === 0) return null;
        return Math.max(...offers.map(offer => offer.offer_price));
    };
    
    const calculateDiscountPercentage = (maxPrice, buyNowPrice) => {
        if (!maxPrice || !buyNowPrice || maxPrice <= buyNowPrice) return null;
        const discount = ((maxPrice - buyNowPrice) / maxPrice) * 100;
        return Math.floor(discount);
    };

    const highestOffer = getHighestOffer(ticket.offers);
    const discountPercentage = calculateDiscountPercentage(ticket.maxPrice, ticket.buyNowPrice);

    // Calcola il prezzo da mostrare considerando finalPrice (con coupon) o buyNowPrice + penaltyPrice
    const displayPrice = React.useMemo(() => {
        // Se esiste finalPrice (coupon applicato), usalo
        if (ticket.finalPrice !== undefined && ticket.finalPrice !== null) {
            return Math.floor(ticket.finalPrice);
        }
        // Altrimenti usa buyNowPrice + penaltyPrice
        const basePrice = ticket.buyNowPrice || 0;
        const penalty = ticket.penaltyPrice || 0;
        return Math.floor(basePrice + penalty);
    }, [ticket.finalPrice, ticket.buyNowPrice, ticket.penaltyPrice]);

    const cardUrl = `${window.location.origin}/ticket/room/${ticket._id}`;
    const hotelImagePath = getHotelImageUrl(
        ticket?.hotelDetails?.localImagePaths?.[0] ?? ''
    );

    const handleFavoriteClick = (e) => {
        e.stopPropagation();
    };

    const handleShareClick = (e) => {
        e.stopPropagation();
    };

    const handleMouseMove = (e) => {
        const card = cardRef.current;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const rotateX = (y - centerY) / 25;
        const rotateY = (centerX - x) / 25;

        const updateTransform = () => {
            setTransformStyle(`perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`);
        };

        cancelAnimationFrame(requestRef.current);
        requestRef.current = requestAnimationFrame(updateTransform);
    };

    const handleMouseLeave = () => {
        // Cancella qualsiasi requestAnimationFrame pendente
        cancelAnimationFrame(requestRef.current);
        // Ripristina la posizione originale
        setTransformStyle('perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)');
    };

    useEffect(() => {
        return () => {
            cancelAnimationFrame(requestRef.current);
        };
    }, []);

    function truncateText(text, maxLength) {
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    }

    return (
        <div
            ref={cardRef}
            className="bg-white rounded-2xl shadow-lg overflow-hidden w-full sm:w-96 relative transition-transform duration-500 ease-out"
            style={{ transform: transformStyle }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <Link
                to={`/ticket/room/${ticket._id}`}
                state={{ user: ticket.user_id }}
                className="block h-full"
            >
                <div className="h-52 bg-gray-200 relative">
                    <div className="absolute top-5 left-5 w-1/3">
                        <CountdownPreview expiryDate={ticket.auctionEndTime} auctionStatus={ticket.auctionStatus} />
                    </div>
                    <img
                        src={hotelImagePath}
                        alt={ticket.name}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="p-2 rounded-2xl p-4">
                    <div className="flex justify-start">
                        <img src={Alloggio} className="w-6 pb-4 mr-2" alt="Alloggio Icona" />
                        <h2 className="text-lg font-semibold mb-2 font-heading uppercase text-xl">{t('sell.room')}</h2>
                    </div>
                    <div className="flex items-center space-x-4 whitespace-nowrap mb-2">
                        <h2 className="text-base font-semibold">
                            {truncateText(ticket.hotelDetails.name, 20)} •
                        </h2>
                        <div className="flex items-center space-x-2">
                            <img src={Single} className="w-6" alt="Icona persone" />
                            <p className="text-md">{ticket.numberPeople}</p>
                        </div>

                        {ticket.numberChildren > 0 && (
                            <div className="flex items-center space-x-2">
                                <img src={Bambini} className="w-6" alt="Icona bambini" />
                                <p className="text-md">{ticket.numberChildren}</p>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-2 mb-2">
                        <p className="text-base ">
                            {ticket?.hotelDetails?.city} •
                        </p>
                        <p className="text-base">
                            {ticket?.hotelDetails?.country}
                        </p>
                    </div>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <p className="text-base font-semibold text-burnt-coffee">
                                {new Date(ticket.roomDateStart).toLocaleDateString('it-IT', { day: 'numeric', month: 'numeric' })}  •
                            </p>
                            <p className="text-base font-semibold text-burnt-coffee">
                                {new Date(ticket.roomDateEnd).toLocaleDateString('it-IT', { day: 'numeric', month: 'numeric', year: 'numeric' })}
                            </p>
                        </div>
                        <div>
                            <span className="text-lg font-semibold text-wild-ocean line-through opacity-50">
                                {convertCurrencySymbol(ticket.currency)}{ticket.maxPrice}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        {discountPercentage && (
                            <span className="text-xl font-bold text-wild-ocean mr-2">
                                -{discountPercentage}%
                            </span>
                        )}
                        <span className="text-xl font-bold text-burnt-coffee">
                            {convertCurrencySymbol(ticket.currency)}{highestOffer || displayPrice}
                        </span>
                    </div>
                </div>
            </Link>

            <div className="absolute right-2 flex space-x-4 top-1/2">
                <div onClick={handleFavoriteClick}>
                    <FavoriteButton itemId={ticket._id} type="rooms" />
                </div>
                <div onClick={handleShareClick}>
                    <ShareButton url={cardUrl} />
                </div>
            </div>
        </div>
    );
};

export default RoomCard;
